import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { logError } from '../utils/logger';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, DollarSign, Users, ArrowRight, Copy, Check, AlertCircle,
  BarChart3, MessageCircle, Building, Target, Sparkles, Receipt, FileText, CheckCircle, Globe
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Waitlist = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [languagePref, setLanguagePref] = useState('en');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [position, setPosition] = useState(null);
  const [referralCode, setReferralCode] = useState('');
  const [foundingRemaining, setFoundingRemaining] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Get referral code from URL
  const refParam = searchParams.get('ref');

  useEffect(() => {
    fetchFoundingCount();
    // Refresh count every 30 seconds for live updates
    const interval = setInterval(fetchFoundingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchFoundingCount = async () => {
    try {
      const response = await axios.get(`${API}/waitlist/founding-count`);
      setFoundingRemaining(response.data.remaining);
    } catch (error) {
      logError(error, { context: 'Waitlist.fetchFoundingCount' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/waitlist/join`, {
        email,
        language_pref: languagePref,
        source: refParam ? 'referral' : 'landing_page',
        referred_by: refParam || null
      });

      setPosition(response.data.position);
      setReferralCode(response.data.referral_code);
      setJoined(true);
      toast.success(`You're #${response.data.position} on the waitlist!`);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyReferralLink = () => {
    const link = `${window.location.origin}/waitlist?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFoundingClick = () => {
    // Navigate to founding checkout
    navigate(`/waitlist/founding?email=${encodeURIComponent(email)}`);
  };

  if (joined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <CardTitle className="text-3xl">You're on the list!</CardTitle>
            <CardDescription className="text-lg">
              You're <strong>#{position}</strong> in line for early access
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {foundingRemaining > 0 && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6">
                <h3 className="text-lg font-bold text-amber-900 mb-2">
                  ⚡ Skip the Wait - Become a Founding Member
                </h3>
                <p className="text-amber-800 mb-4">
                  <strong>Only {foundingRemaining} of 100 spots remaining!</strong><br />
                  Lock in <span className="text-2xl font-bold">$79/year</span> forever 
                  <span className="line-through text-gray-500 ml-2">$228/year</span>
                </p>
                <Button 
                  onClick={handleFoundingClick}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white text-lg py-6"
                >
                  Claim Your Founding Spot →
                </Button>
              </div>
            )}

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-emerald-900 mb-3">
                🎁 Share & Earn $20 Credit
              </h3>
              <p className="text-emerald-800 mb-4 text-sm">
                When 3 friends you refer become founding members, you'll get a $20 credit!
              </p>
              
              <Label className="text-sm text-emerald-700 mb-2 block">Your Referral Link:</Label>
              <div className="flex gap-2">
                <Input 
                  value={`${window.location.origin}/waitlist?ref=${referralCode}`}
                  readOnly
                  className="bg-white"
                />
                <Button
                  onClick={handleCopyReferralLink}
                  variant="outline"
                  className="flex-shrink-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="text-center text-gray-600">
              <p className="mb-2">Check your email for confirmation and next steps!</p>
              <Button variant="link" onClick={() => navigate('/')}>
                ← Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900">
      {/* Hero Section with Waitlist Form */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/50 mb-4">
            ✨ AI-Powered Tax Filing
          </Badge>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-heading">
            File Complex Taxes<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-600">
              Confidently
            </span>
          </h1>
          
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            AI-powered tax management for 1099 contractors and sole entrepreneurs. 
            Track income, maximize deductions, and file with confidence.
          </p>
          
          <button
            onClick={() => navigate('/how-it-works')}
            className="text-emerald-400 hover:text-emerald-300 underline mb-8 text-lg"
          >
            See How It Works (8 Steps with Screenshots) →
          </button>

          {/* Waitlist Form */}
          <Card className="max-w-md mx-auto bg-white/95 backdrop-blur">
            <CardHeader>
              <CardTitle>Join the Waitlist</CardTitle>
              <CardDescription>
                Be among the first to access SolelyTax AI
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Preferred Language</Label>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={languagePref === 'en' ? 'default' : 'outline'}
                      onClick={() => setLanguagePref('en')}
                      className="flex-1"
                    >
                      English
                    </Button>
                    <Button
                      type="button"
                      variant={languagePref === 'es' ? 'default' : 'outline'}
                      onClick={() => setLanguagePref('es')}
                      className="flex-1"
                    >
                      Español
                    </Button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
                  disabled={loading}
                >
                  {loading ? 'Joining...' : 'Join Waitlist'}
                  {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
                </Button>

                {refParam && (
                  <p className="text-xs text-center text-emerald-600">
                    ✨ Referred by a friend! You'll move up the list.
                  </p>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Founding Member Urgency Counter */}
          {foundingRemaining !== null && foundingRemaining > 0 && (
            <div className="mt-8 bg-amber-500/20 border-2 border-amber-500 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-amber-300 font-bold text-lg">
                ⚡ LIMITED TIME: {foundingRemaining} of 100 Founding Spots Left!
              </p>
              <p className="text-amber-200 text-sm mt-2">
                Lock in $79/year forever (saves you $149/year)
              </p>
            </div>
          )}
        </div>

        {/* Problem Statement - White Background */}
        <div className="max-w-5xl mx-auto mb-20 bg-white py-12 px-4 rounded-2xl">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
            Tax Season Shouldn't Be This Hard
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: AlertCircle,
                title: "Missed Deductions",
                description: "Leave money on the table because you don't know what's deductible"
              },
              {
                icon: Receipt,
                title: "Receipt Chaos",
                description: "Shoeboxes of receipts, missing documentation, disorganized records"
              },
              {
                icon: DollarSign,
                title: "Surprise Tax Bills",
                description: "No idea what you owe until April 15th - then panic"
              }
            ].map((pain, idx) => (
              <Card key={idx} className="bg-slate-50 hover:bg-white border-2 border-gray-200 hover:border-emerald-200 transition-all">
                <CardContent className="pt-6">
                  <pain.icon className="w-12 h-12 text-red-500 mb-4" />
                  <h3 className="text-gray-900 font-bold mb-2">{pain.title}</h3>
                  <p className="text-gray-600 text-sm">{pain.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Product Overview - White Background */}
        <div className="max-w-5xl mx-auto mb-20 bg-white py-12 px-4 rounded-2xl">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-6">
            Meet Your AI Tax Assistant
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            SolelyTax AI automatically tracks your income, finds every deduction, and keeps you 
            organized year-round. No more scrambling at tax time.
          </p>
          
          <div className="bg-white backdrop-blur border-2 border-gray-200 rounded-xl p-2 shadow-lg">
            <img 
              src="/screenshots/advisors.png"
              alt="Choose Your AI Tax Advisor - Carlotta Thompson, Justin Wu, Sabrina Chen, David Martinez"
              className="w-full rounded-lg"
            />
          </div>
        </div>

        {/* Feature Grid - White Background */}
        <div className="max-w-5xl mx-auto mb-20 bg-white py-12 px-4 rounded-2xl">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
            Everything You Need in One Place
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Sparkles,
                title: "AI Categorization",
                description: "Automatically categorize every transaction with 95% accuracy"
              },
              {
                icon: FileText,
                title: "CSV Import",
                description: "Upload bank statements - we'll sort everything instantly"
              },
              {
                icon: Globe,
                title: "Multi-State Support",
                description: "Accurate tax calculations for all 50 states"
              },
              {
                icon: Target,
                title: "Deduction Finder",
                description: "AI discovers deductions you didn't know existed"
              },
              {
                icon: BarChart3,
                title: "Real-Time Estimates",
                description: "Know your tax bill at any moment - no surprises"
              },
              {
                icon: MessageCircle,
                title: "AI Tax Advisors",
                description: "Chat with 4 specialized tax experts anytime"
              }
            ].map((feature, idx) => (
              <Card key={idx} className="bg-slate-50 hover:bg-white border-2 border-gray-200 hover:border-emerald-300 transition-all">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-gray-900 font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Pricing Cards - White Background */}
        <div className="max-w-5xl mx-auto mb-20 bg-white py-12 px-4 rounded-2xl">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
            Simple, Transparent Pricing
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Founding Member Card */}
            <Card className="border-4 border-amber-500 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-amber-500 text-white px-4 py-1 text-xs font-bold">
                BEST VALUE
              </div>
              <CardHeader>
                <Badge className="bg-amber-100 text-amber-800 mb-2 w-fit">
                  {foundingRemaining > 0 ? `${foundingRemaining} spots left` : 'SOLD OUT'}
                </Badge>
                <CardTitle>Founding Member</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$79</span>
                  <span className="text-gray-600">/year</span>
                </div>
                <p className="text-sm text-gray-600 line-through">Regular: $228/year</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-6">
                  {[
                    "Everything included",
                    "Locked-in price forever",
                    "Priority support",
                    "Early feature access",
                    "Founding Member badge"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-amber-600 font-medium mb-3">
                  🔥 Only {foundingRemaining} of 100 spots available
                </p>
              </CardContent>
            </Card>

            {/* Early Access Card */}
            <Card className="border-2 border-emerald-500">
              <CardHeader>
                <Badge className="bg-emerald-100 text-emerald-800 mb-2 w-fit">
                  AFTER FOUNDING
                </Badge>
                <CardTitle>Early Access</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$99</span>
                  <span className="text-gray-600">/year</span>
                </div>
                <p className="text-sm text-gray-600">Spots 101-500</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {[
                    "Everything included",
                    "Priority onboarding",
                    "Early feature access",
                    "Email support"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Standard Card */}
            <Card className="border border-gray-300">
              <CardHeader>
                <Badge className="bg-gray-100 text-gray-800 mb-2 w-fit">
                  STANDARD
                </Badge>
                <CardTitle>Standard</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold">$19</span>
                  <span className="text-gray-600">/month</span>
                </div>
                <p className="text-sm text-gray-600">$228/year</p>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {[
                    "Unlimited transactions",
                    "AI categorization",
                    "Bank sync",
                    "All reports",
                    "Email support"
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Final CTA */}
        <div className="max-w-2xl mx-auto text-center">
          <Card className="bg-white/95 backdrop-blur">
            <CardContent className="pt-8 pb-8">
              <h2 className="text-2xl font-bold mb-4">Ready to Take Control of Your Taxes?</h2>
              <p className="text-gray-600 mb-6">
                Join hundreds of freelancers and business owners who are simplifying tax season.
              </p>
              <form onSubmit={handleSubmit} className="flex gap-3">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="flex-1"
                />
                <Button 
                  type="submit" 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                >
                  {loading ? 'Joining...' : 'Join Waitlist'}
                </Button>
              </form>
              <p className="text-xs text-gray-500 mt-4">
                No spam. Unsubscribe anytime. Read our{' '}
                <button onClick={() => navigate('/privacy')} className="text-emerald-600 hover:underline">
                  Privacy Policy
                </button>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
            <button onClick={() => navigate('/terms')} className="hover:text-emerald-400 transition-colors">
              Terms
            </button>
            <span>•</span>
            <button onClick={() => navigate('/privacy')} className="hover:text-emerald-400 transition-colors">
              Privacy
            </button>
            <span>•</span>
            <a href="mailto:support@solelytax.ai" className="hover:text-emerald-400 transition-colors">
              Support
            </a>
          </div>
          <p className="text-center text-slate-500 text-xs mt-4">
            © 2026 SolelyTax AI. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Waitlist;
